import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { TotalPlaylistReachData } from '../../services/api/artist-analytics.service';

interface TotalPlaylistReachChartProps {
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const TotalPlaylistReachChart: React.FC<TotalPlaylistReachChartProps> = ({
  artistId,
  title = 'Total Playlist Count',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<TotalPlaylistReachData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artistName, setArtistName] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getTotalPlaylistReach(artistId);
        setData(response.data);
        setArtistName(response.artistName);
      } catch (err) {
        setError('Failed to load playlist count data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, refreshKey]);

  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000000000) {
      return `${(num / 1000000000000).toFixed(1)}T`;
    } else if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Platform colors
    const platformColors: { [key: string]: string } = {
      'Spotify': '#1DB954',
      'Apple Music': '#FA233B',
      'YouTube Music': '#FF0000',
      'Deezer': '#FF6600',
      'Amazon Music': '#FF9900',
      'Pandora': '#005583',
      'SoundCloud': '#FF5500'
    };

    const chartData = data.map(item => ({
      name: item.platform_name,
      value: item.total_reach,
      itemStyle: {
        color: platformColors[item.platform_name] || '#6366f1'
      }
    }));

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        formatter: function(params: any) {
          const param = params[0];
          const platform = param.name;
          const reach = formatLargeNumber(param.value);
          const dataItem = data.find(d => d.platform_name === platform);
          
          if (dataItem) {
            return `
              <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${platform}</div>
              <div style="color: ${param.color}; margin-bottom: 4px;">● Total Reach: ${reach}</div>
              <div style="color: #9ca3af; font-size: 11px;">Total Playlists: ${formatLargeNumber(dataItem.total_playlists)}</div>
            `;
          }
          return `<div style="color: ${param.color};">● ${platform}: ${reach}</div>`;
        }
      },
      grid: {
        left: 20,
        right: 20,
        top: 15,
        bottom: 40,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        show: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { 
          show: true,
          lineStyle: {
            color: '#e5e7eb',
            type: 'solid',
            width: 1
          }
        }
      },
      yAxis: {
        type: 'category',
        data: chartData.map(item => item.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#6b7280',
          fontSize: 11,
          fontWeight: 500,
          interval: 0,
          margin: 8
        },
        splitLine: { show: false }
      },
      series: [
        {
          type: 'bar',
          data: chartData,
          barWidth: '70%',
          barCategoryGap: '20%',
          itemStyle: {
            borderRadius: [0, 4, 4, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          label: {
            show: true,
            position: 'right',
            color: '#1f2937',
            fontSize: 10,
            fontWeight: 600,
            formatter: function(params: any) {
              return formatLargeNumber(params.value);
            }
          },
          animationDelay: function (idx: number) {
            return idx * 100;
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent px-2 pt-2 pb-1">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default TotalPlaylistReachChart;
